// ═══════════════════════════════════════
// filtrosPermiso.js — catálogo cerrado de FILTROS DECLARATIVOS por módulo
// ═══════════════════════════════════════
// Cada permiso de un rol puede declarar filtros adicionales al scope:
//
//   { modulo:'casos', accion:'ver', scope:'todos',
//     filtros: { tipoCaso: ['impuestos', 'facturacion'], area: ['contable'] } }
//
// Significa: "ver TODOS los casos de tipo {impuestos, facturacion} cuyo área sea contable".
//
// Los filtros disponibles son un CATÁLOGO CERRADO por módulo definido en código.
// No es DSL libre; está hardcoded para garantizar consistencia y performance.
//
// Cada entrada de filtro tiene:
//   - label: string para el editor de roles.
//   - tipo: 'select-static' | 'select-dynamic' | 'numeric-max' | 'numeric-min' | 'boolean'
//   - opciones: array de valores cuando tipo=select-static.
//   - fuente: nombre de colección Firestore cuando tipo=select-dynamic.
//   - multi: si admite selección múltiple (default true para selects).
//   - aplicar(recurso, valor): función que evalúa si el recurso pasa el filtro.
//
// Cuando un permiso NO tiene `filtros` declarados → modo amplio (no se filtra).
// Cuando tiene → se aplica AND entre filtros.
// ═══════════════════════════════════════

const PRIORIDADES_DEFAULT = ['baja', 'normal', 'alta', 'critica']
const AREAS_DEFAULT = [
  'contable',
  'comercial',
  'logistica',
  'atencion-cliente',
  'tecnico',
  'marketing',
  'ventas',
  'fiscal',
  'rrhh',
]
const VISIBILIDAD_DEFAULT = ['normal', 'restringido', 'confidencial']

// Helper para chequear "valor de un campo del recurso pasa por el filtro multi".
function passSelectMulti(recursoValor, valoresPermitidos) {
  if (!valoresPermitidos || valoresPermitidos.length === 0) return true
  if (recursoValor == null) return false
  if (Array.isArray(recursoValor)) {
    return recursoValor.some(v => valoresPermitidos.includes(v))
  }
  return valoresPermitidos.includes(recursoValor)
}

// Helper para chequear "prioridad del recurso <= prioridadMaxima del filtro".
const ORDEN_PRIORIDAD = { baja: 1, normal: 2, alta: 3, critica: 4 }
function passPrioridadMaxima(recursoPrioridad, prioridadMaxima) {
  if (!prioridadMaxima) return true
  if (!recursoPrioridad) return true
  const max = ORDEN_PRIORIDAD[prioridadMaxima] ?? 99
  const rec = ORDEN_PRIORIDAD[recursoPrioridad] ?? 0
  return rec <= max
}

export const FILTROS_PERMISO = Object.freeze({
  casos: {
    tipoCaso: {
      label: 'Tipo de caso',
      tipo: 'select-dynamic',
      fuente: 'tiposCaso',           // colección Firestore — el editor lee de ahí
      multi: true,
      aplicar: (caso, valores) => passSelectMulti(caso?.tipo, valores),
    },
    categoria: {
      label: 'Categoría',
      tipo: 'select-dynamic',
      fuente: 'categoriasCaso',
      multi: true,
      aplicar: (caso, valores) => passSelectMulti(caso?.categoria, valores),
    },
    area: {
      label: 'Área',
      tipo: 'select-static',
      opciones: AREAS_DEFAULT,
      multi: true,
      aplicar: (caso, valores) => passSelectMulti(caso?.area || caso?.tipoCaso?.area, valores),
    },
    prioridadMaxima: {
      label: 'Prioridad máxima',
      tipo: 'select-static',
      opciones: PRIORIDADES_DEFAULT,
      multi: false,
      aplicar: (caso, valor) => passPrioridadMaxima(caso?.prioridad, valor),
    },
    visibilidad: {
      label: 'Visibilidad permitida',
      tipo: 'select-static',
      opciones: VISIBILIDAD_DEFAULT,
      multi: true,
      aplicar: (caso, valores) => passSelectMulti(caso?.visibilidad || 'normal', valores),
    },
  },

  accionables: {
    area: {
      label: 'Área',
      tipo: 'select-static',
      opciones: AREAS_DEFAULT,
      multi: true,
      aplicar: (tarea, valores) => passSelectMulti(tarea?.area, valores),
    },
    tipo: {
      label: 'Tipo de tarea',
      tipo: 'select-dynamic',
      fuente: 'tiposTarea',
      multi: true,
      aplicar: (tarea, valores) => passSelectMulti(tarea?.tipo, valores),
    },
    prioridadMaxima: {
      label: 'Prioridad máxima',
      tipo: 'select-static',
      opciones: PRIORIDADES_DEFAULT,
      multi: false,
      aplicar: (tarea, valor) => passPrioridadMaxima(tarea?.prioridad, valor),
    },
  },

  dashboards: {
    area: {
      label: 'Área',
      tipo: 'select-static',
      opciones: AREAS_DEFAULT,
      multi: true,
      aplicar: (dashboard, valores) => passSelectMulti(dashboard?.area, valores),
    },
  },

  reuniones: {
    area: {
      label: 'Área',
      tipo: 'select-static',
      opciones: AREAS_DEFAULT,
      multi: true,
      aplicar: (reu, valores) => passSelectMulti(reu?.area, valores),
    },
  },

  // Otros módulos no tienen filtros declarativos (todos los permisos son amplios).
  // Agregar entradas acá cuando aparezca la necesidad.
})

/**
 * Devuelve los filtros disponibles para un módulo (para construir el editor).
 * Retorna {} si el módulo no tiene filtros declarados.
 */
export function filtrosDe(moduloCodigo) {
  return FILTROS_PERMISO[moduloCodigo] || {}
}

/**
 * Evalúa si un recurso pasa por TODOS los filtros declarados en `filtros`
 * para el módulo dado. Si `filtros` es null/undefined/{}, retorna true.
 *
 * @param {string} moduloCodigo
 * @param {Object} recurso          — doc del recurso (caso, tarea, etc.)
 * @param {Object|null} filtros     — { tipoCaso: [...], area: [...] } o null
 * @returns {boolean}
 */
export function recursoPasaFiltros(moduloCodigo, recurso, filtros) {
  if (!filtros) return true
  if (typeof filtros !== 'object') return true
  const keys = Object.keys(filtros)
  if (keys.length === 0) return true

  const catalogo = FILTROS_PERMISO[moduloCodigo]
  if (!catalogo) return true   // módulo sin filtros declarados — pasa todo

  for (const key of keys) {
    const filtroDef = catalogo[key]
    if (!filtroDef) continue   // filtro desconocido — ignorar (no romper)
    const valorFiltro = filtros[key]
    const ok = filtroDef.aplicar(recurso, valorFiltro)
    if (!ok) return false
  }
  return true
}
